import React, { useState } from "react";
import { Button, Form, Row, Col, Card, Modal } from "react-bootstrap";
import { BsThreeDotsVertical, BsTrash } from "react-icons/bs";
import { FaEdit } from "react-icons/fa";
import CreatePartnerModal from "./CreatePartenerModal";
import { useGetPartners } from "../../api/queries/useGetPartners";
import { useDeletePartner } from "../../api/mutations/useDeletePartner";
import { Partner } from "../../types/Types";

const CreatePartners: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

  const { data, refetch: refetchPartners } = useGetPartners({
    pageSize: 25,
    page: 1,
  });
  const { mutate: deleteEvent, isPending: isDeleting } = useDeletePartner({
    onSuccess: () => {
      setDeleteModal(false);
      setSelectedPartner(null);
      refetchPartners();
    },
  });

  const mergedPartners = (data?.partners.data ?? []).map((partner) => {
    const imageEntry = data?.partnerImageUrls?.find(
      (img) => img.partnerId === partner.id
    );

    return {
      ...partner,
      ...imageEntry?.imageUrls,
    };
  });

  const handleEdit = (partner: Partner) => {
    setSelectedPartner(partner);
    setShowModal(true);
  };

  const handleDeleteClick = (partner: Partner) => {
    setSelectedPartner(partner);
    setDeleteModal(true);
  };

  const confirmDelete = () => {
    if (selectedPartner) {
      deleteEvent(selectedPartner.id);
    }
  };

  return (
    <div className="p-3">
      <div className="d-flex gap-4 flex-column flex-md-row justify-content-between align-items-center mb-3">
        <Form.Control
          type="text"
          placeholder="Search partners..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          variant="primary"
          className="shadow-sm rounded-pill"
          onClick={() => setShowModal(true)}
        >
          Create Partner
        </Button>
      </div>
      <Row>
        {mergedPartners
          .filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
          .map((partner, idx) => (
            <Col md={4} key={partner.id} className="mb-3">
              <Card className="shadow-sm border-0 rounded-3 p-3 h-100 position-relative">
                {/* Dropdown Menu */}
                <div className="dropdown position-absolute top-0 end-0 m-2">
                  <button
                    className="btn btn-link p-0"
                    type="button"
                    id={`dropdownMenuButton-${idx}`}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <BsThreeDotsVertical size={20} />
                  </button>
                  <ul
                    className="dropdown-menu mt-1"
                    aria-labelledby={`dropdownMenuButton-${idx}`}
                  >
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => handleEdit(partner)}
                      >
                        <FaEdit size={16} className="me-2" />
                        Edit
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item text-danger"
                        onClick={() => handleDeleteClick(partner)}
                      >
                        <BsTrash size={16} className="me-2" />
                        Delete
                      </button>
                    </li>
                  </ul>
                </div>

                {/* Content */}
                <Card.Body>
                  <Card.Title className="fw-bold">{partner.name}</Card.Title>
                  <Card.Text className="text-muted small text-truncate d-block">
                    {partner.description}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
      </Row>

      {/* Create Partner Modal */}
      <CreatePartnerModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSelectedPartner(null);
        }}
        defaultData={selectedPartner}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal} onHide={() => setDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{selectedPartner?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            disabled={isDeleting}
            variant="danger"
            onClick={confirmDelete}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CreatePartners;
